import { Translations } from "@/translations";
import { useTranslations } from "next-intl";
import { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import Link from "next/link";
import { useEffect } from "react";

interface PaginationControlsProps {
  router: AppRouterInstance,
  currentPage: number,
  page: number,
  totalPages: number;
  basePath: string;
  setPage: (p: number) => void;
}

const buildPageHref = (basePath: string, page: number) => {
  const separator = basePath.includes("?") ? "&" : "?";
  return `${basePath}${separator}page=${page}`;
};

const PaginationControls = ({
  router,
  currentPage,
  page,
  totalPages,
  basePath,
  setPage
}: PaginationControlsProps) => {
  const t = useTranslations("Courses");

  useEffect(() => {
    if (totalPages <= 0) {
      setPage(1);
      return;
    }

    if (page < 1 || page > totalPages) {
      router.replace(buildPageHref(basePath, Math.max(1, Math.min(page, totalPages))));
    } else {
      setPage(page);
    }
  }, [basePath, page, router, setPage, totalPages]);

  if (totalPages <= 1) {
    return null;
  }

  // array of pages to show in pagination control
  let pagesToShow = [currentPage];
  // add previous number before the current page if is not page 1
  if (currentPage > 1) pagesToShow.unshift(currentPage - 1);
  // add next number after current page if not the last
  if (currentPage < totalPages) pagesToShow.push(currentPage + 1);
  // always show page 1
  if (currentPage > 2) pagesToShow.unshift(1);
  // always show last page
  if (currentPage < totalPages - 1) pagesToShow.push(totalPages);

  // remove duplicates and sort
  pagesToShow = [...new Set(pagesToShow)].sort((a, b) => a - b);

  const previous = (
    <>
      {t(Translations.Courses.Previous)}
      <i className="arrow_left"></i>
      <i className="arrow_left"></i>
    </>
  );

  const next = (
    <>
      {t(Translations.Courses.Next)}
      <i className="arrow_right"></i>
      <i className="arrow_right"></i>
    </>
  );

  return (
    <>
      <div className="row">
        <div className="col-xxl-12">
          <div
            className="basic-pagination wow fadeInUp mt-30"
            data-wow-delay=".2s"
          >
            <ul className="d-flex align-items-center">
              {/* previous button */}
              <li className="prev" key="prev">
                {currentPage > 1 ? (
                  <Link
                    href={buildPageHref(basePath, currentPage - 1)}
                    className="link-btn link-prev"
                  >
                    {previous}
                  </Link>
                ) : (
                  <span>{previous}</span>
                )}
              </li>

              {/* page numbers */}
              {pagesToShow.map((page) => (
                <li  key={page}>
                  {page === currentPage ? (
                    <span className="active">{page}</span>
                  ) : (
                    <Link href={buildPageHref(basePath, page)}>
                      <span>{page}</span>
                    </Link>
                  )}
                </li>
              ))}

              {/* next page button */}
              <li className="next" key="next">
                {currentPage < totalPages ? (
                  <Link
                    href={buildPageHref(basePath, currentPage + 1)}
                    className="link-btn"
                  >
                    {next}
                  </Link>
                ) : (
                  <span>{next}</span>
                )}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
};

export default PaginationControls;
